import React, { useEffect, useRef, useState } from "react";
import { Typography, Button } from "@mui/material";
import { t } from "i18next";
import { PicassoChallengeResult } from "../../shared/interfaces/GameLogicTypes";
import { sanitizeSvg, nextSvgUid } from "../SvgDrawing/SvgDrawing";
import "./PicassoImposterReveal.css";

interface PicassoImposterRevealProps {
  drawingA: string;
  drawingB: string;
  targetPlayerName: string;
  playerId: string;
  onVote: (vote: "A" | "B") => void;
  onSelect?: (vote: "A" | "B" | null) => void;
  /** When set, the drawing cards flip to reveal who drew them */
  revealResult?: PicassoChallengeResult | null;
  onRevealComplete?: () => void;
}

export default function PicassoImposterReveal({
  drawingA, drawingB, targetPlayerName, playerId, onVote, onSelect,
  revealResult, onRevealComplete,
}: PicassoImposterRevealProps) {
  const [selectedVote, setSelectedVote] = useState<"A" | "B" | null>(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const uidA = useRef(nextSvgUid()).current;
  const uidB = useRef(nextSvgUid()).current;

  const revealing = !!revealResult;

  useEffect(() => {
    if (!revealResult) {
      setFlipped(false);
      return;
    }
    const flipTimer = setTimeout(() => setFlipped(true), 300);
    const completeTimer = setTimeout(() => onRevealComplete?.(), 3000);
    return () => {
      clearTimeout(flipTimer);
      clearTimeout(completeTimer);
    };
  }, [revealResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVote = (vote: "A" | "B") => {
    if (hasVoted || revealing) return;
    setSelectedVote(vote);
    onSelect?.(vote);
  };

  const handleSubmit = () => {
    if (!selectedVote || hasVoted || revealing) return;
    setHasVoted(true);
    onVote(selectedVote);
  };

  const getBackLabel = (side: "A" | "B"): string => {
    if (!revealResult) return "";
    const isReal = side === revealResult.realLabel;
    if (isReal) {
      return side === "A" ? revealResult.drawerAName : revealResult.drawerBName;
    }
    if (revealResult.type === "imposter") {
      return `${revealResult.imposterName ?? "???"} (Imposter)`;
    }
    if (revealResult.type === "archive") {
      return t("picasso_from_another_lobby");
    }
    return side === "A" ? revealResult.drawerAName : revealResult.drawerBName;
  };

  const sides: ("A" | "B")[] = ["A", "B"];

  return (
    <div className="picasso-imposter-container">
      <Typography variant="h5" color="white" fontWeight="bold">
        {`${t("picasso_which_true_answer")} ${targetPlayerName}?`}
      </Typography>

      <div className="picasso-imposter-drawings">
        {sides.map((side) => {
          const drawing = side === "A" ? drawingA : drawingB;
          const uid = side === "A" ? uidA : uidB;
          const sanitized = sanitizeSvg(drawing, uid);
          const isSelected = selectedVote === side;
          const isReal = revealResult ? side === revealResult.realLabel : false;

          if (revealing) {
            return (
              <div key={side} className="picasso-imposter-drawing-card">
                <span className="picasso-imposter-label">{side}</span>
                <div className="picasso-flip">
                  <div className={`picasso-flip-inner${flipped ? " flipped" : ""}`}>
                    <div
                      className={`drawing-frame picasso-flip-front${isSelected ? ` selected-${side.toLowerCase()}` : ""}`}
                      dangerouslySetInnerHTML={{ __html: sanitized }}
                    />
                    <div className={`picasso-flip-back ${isReal ? "correct" : "wrong"}`}>
                      <Typography variant="h6" fontWeight="bold" color="white" textAlign="center"
                        sx={{ textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                        {getBackLabel(side)}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={side} className="picasso-imposter-drawing-card">
              <span className="picasso-imposter-label">{side}</span>
              <div
                className={`drawing-frame${isSelected ? ` selected-${side.toLowerCase()}` : ""}`}
                onClick={() => handleVote(side)}
                dangerouslySetInnerHTML={{ __html: sanitized }}
              />
            </div>
          );
        })}
      </div>

      {revealing ? null : hasVoted ? (
        <Typography color="grey.500" fontStyle="italic">
          {t("waiting_for_others")}
        </Typography>
      ) : (
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedVote}
        >
          {t("picasso_confirm_vote")}
        </Button>
      )}
    </div>
  );
}
